import { ref, watch } from 'vue';
import { type TextSelectMode } from '@s-blog/shared-types';

/**
 * 文字选择偏好 (模块级单例)。两层模型: localStorage 覆盖 > 站长默认 (applyOwnerDefault) > 内置默认 'off'。
 * 默认**关闭**鼠标滑动选择 — 配合 shader 搅动手势防误选 + 触屏长按不弹系统菜单。需要复制时 /settings 打开。
 * 输入框/textarea/contenteditable 始终豁免 (global.css)。
 * **持久化只在显式 setter 时写** —— body class 副作用每次跑, 落盘只在访客主动改时, 保住站长默认能生效。
 */
export type { TextSelectMode };

const STORAGE_KEY = 'aurora-text-select';

const stored: TextSelectMode | null = (() => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s === 'on' || s === 'off') return s;
  } catch {
    // SSR / privacy mode
  }
  return null;
})();
const hadStored = stored !== null;
const textSelect = ref<TextSelectMode>(stored ?? 'off');

// body class 副作用 (每次都跑, 不落盘)
watch(
  textSelect,
  (v) => {
    document.body.classList.toggle('text-select-off', v === 'off');
  },
  { immediate: true },
);

function persist(v: TextSelectMode) {
  try {
    localStorage.setItem(STORAGE_KEY, v);
  } catch {
    // ignore
  }
}

export function useTextSelect() {
  function setTextSelect(v: TextSelectMode) {
    textSelect.value = v;
    persist(v);
  }
  function toggleTextSelect() {
    setTextSelect(textSelect.value === 'on' ? 'off' : 'on');
  }
  /** 站长默认: 仅当访客从没设过时套用, 不落盘 */
  function applyOwnerDefault(v: TextSelectMode | null | undefined) {
    if (!hadStored && (v === 'on' || v === 'off')) textSelect.value = v;
  }
  return { textSelect, setTextSelect, toggleTextSelect, applyOwnerDefault };
}
